import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Chip, CircularProgress, Divider, Grid, LinearProgress,
  Stack, Table, TableBody, TableCell, TableContainer, TableRow,
  Paper, Typography, Alert, Button, Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as OkIcon,
  Cancel as DownIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

/**
 * El monitoreo, resumido para quien no vive en Grafana.
 *
 * Grafana tiene las gráficas y el historial; esto responde de un vistazo las tres preguntas
 * que se hacen a diario: **¿está todo arriba?**, **¿cómo van los servidores?** y **¿qué está
 * fallando ahora?**. Para lo demás hay un enlace a Grafana.
 */

const GRAFANA = 'https://monitor.driports.com';

interface Servidor {
  nombre: string;
  memoria?: number;
  disco?: number;
  cpu?: number;
  conexiones?: number;
}

const NOMBRES: Record<string, string> = {
  portal: 'Portal + Bot',
  traccar: 'Traccar',
};

// Verde hasta el 70%, ámbar hasta el 85, rojo por encima. Los umbrales son los mismos con los
// que Grafana dispara las alertas: si la barra está roja, hay una alerta a punto de saltar.
const colorPorcentaje = (v?: number) =>
  v === undefined ? 'inherit' : v >= 85 ? 'error' : v >= 70 ? 'warning' : 'success';

const Medidor: React.FC<{ etiqueta: string; valor?: number; sufijo?: string }> = ({ etiqueta, valor, sufijo = '%' }) => (
  <Box sx={{ mb: 1.5 }}>
    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
      <Typography variant="body2" color="text.secondary">{etiqueta}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {valor === undefined ? '—' : `${valor}${sufijo}`}
      </Typography>
    </Stack>
    <LinearProgress
      variant="determinate"
      value={Math.min(100, valor ?? 0)}
      color={colorPorcentaje(valor) as any}
      sx={{ height: 8, borderRadius: 4 }}
    />
  </Box>
);

const MonitoringPage: React.FC = () => {
  const [resumen, setResumen] = useState<any>(null);
  const [salud, setSalud] = useState<any>(null);
  const [reglas, setReglas] = useState<any[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setError(null);
    try {
      const [r, s, g] = await Promise.all([
        apiService.getMonitoringResumen(),
        apiService.getMonitoringSalud().catch(() => null),
        apiService.getMonitoringReglas().catch(() => ({ reglas: [] })),
      ]);
      setResumen(r);
      setSalud(s);
      setReglas(g?.reglas || []);
      if (r && !r.disponible) setError(r.motivo || 'El monitoreo no está disponible');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo consultar el monitoreo');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargar();
    const t = setInterval(cargar, 30000);
    return () => clearInterval(t);
  }, [cargar]);

  if (cargando) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  const alertas = resumen?.alertas || [];
  const objetivos = resumen?.objetivos || [];
  const servidores: Servidor[] = salud?.servidores || [];
  const caidos = objetivos.filter((o: any) => !o.arriba);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>Monitoreo</Typography>
          <Typography variant="body2" color="text.secondary">
            {resumen?.generadoEn
              ? `Actualizado ${new Date(resumen.generadoEn).toLocaleTimeString('es-DO')} · se refresca solo cada 30 s`
              : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={cargar}>Actualizar</Button>
          <Button variant="outlined" startIcon={<OpenIcon />} href={GRAFANA} target="_blank">
            Abrir Grafana
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Lo primero: ¿hay algo ardiendo? ── */}
      {alertas.length === 0 ? (
        <Alert severity="success" icon={<OkIcon />} sx={{ mb: 3 }}>
          <strong>Todo en orden.</strong> {objetivos.length} objetivos vigilados, ninguna alerta activa.
        </Alert>
      ) : (
        <Card sx={{ mb: 3, borderLeft: '5px solid', borderColor: 'error.main' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              {alertas.length} alerta{alertas.length > 1 ? 's' : ''} activa{alertas.length > 1 ? 's' : ''}
            </Typography>
            {alertas.map((a: any) => (
              <Box key={a.id} sx={{ mb: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip
                    label={a.severidad === 'critical' ? 'CRÍTICA' : a.severidad.toUpperCase()}
                    color={a.grave ? 'error' : 'warning'}
                    size="small"
                    sx={{ fontWeight: 700 }}
                  />
                  <Typography sx={{ fontWeight: 600 }}>{a.titulo}</Typography>
                  {a.objetivo && <Chip label={a.objetivo} size="small" variant="outlined" />}
                  <Typography variant="caption" color="text.secondary">
                    desde {new Date(a.desde).toLocaleString('es-DO')}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {a.resumen}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* ── Los servidores ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {servidores.map((s) => (
          <Grid size={{ xs: 12, md: 6 }} key={s.nombre}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {NOMBRES[s.nombre] || s.nombre}
                  </Typography>
                  {s.conexiones !== undefined && (
                    <Tooltip title="Conexiones TCP establecidas. En Traccar es cuántos equipos GPS están conectados de verdad.">
                      <Chip label={`${s.conexiones} conexiones`} size="small" color="info" variant="outlined" />
                    </Tooltip>
                  )}
                </Stack>
                <Medidor etiqueta="Memoria" valor={s.memoria} />
                <Medidor etiqueta="Disco" valor={s.disco} />
                <Medidor etiqueta="CPU" valor={s.cpu} />
              </CardContent>
            </Card>
          </Grid>
        ))}

        {/* El portal por dentro: lo que no se ve mirando el servidor. */}
        {salud?.portal && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>El portal por dentro</Typography>
                <Medidor
                  etiqueta="Memoria contra su tope (al 100% pm2 lo reinicia)"
                  valor={salud.portal.memoriaSobreTope != null
                    ? Math.round(salud.portal.memoriaSobreTope * 1000) / 10 : undefined}
                />
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Consultas a Traccar en cola</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {salud.portal.colaTraccar ?? '—'}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Retraso del bucle de eventos</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {salud.portal.retrasoBucleSeg != null
                      ? `${(salud.portal.retrasoBucleSeg * 1000).toFixed(0)} ms` : '—'}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* ── Qué se está vigilando ── */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Servicios vigilados
                {caidos.length > 0 && (
                  <Chip label={`${caidos.length} caído${caidos.length > 1 ? 's' : ''}`}
                    color="error" size="small" sx={{ ml: 1 }} />
                )}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    {objetivos.map((o: any, i: number) => (
                      <TableRow key={i} hover>
                        <TableCell sx={{ width: 40 }}>
                          {o.arriba
                            ? <OkIcon color="success" fontSize="small" />
                            : <DownIcon color="error" fontSize="small" />}
                        </TableCell>
                        <TableCell>{o.trabajo}</TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>{o.objetivo}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Reglas activas ({reglas.length})
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Lo que se está midiendo. Si alguna se cumple, aparece arriba en rojo.
              </Typography>
              <Divider sx={{ mb: 1 }} />
              {reglas.map((r) => (
                <Stack key={r.uid} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                  <Chip
                    label={r.severidad === 'critical' ? 'CRÍT' : r.severidad.slice(0, 4).toUpperCase()}
                    size="small"
                    color={r.severidad === 'critical' ? 'error' : r.severidad === 'high' ? 'warning' : 'default'}
                    sx={{ minWidth: 56, fontWeight: 700 }}
                  />
                  <Typography variant="body2">{r.titulo}</Typography>
                  <Typography variant="caption" color="text.secondary">({r.durante})</Typography>
                </Stack>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MonitoringPage;
